import { useEffect } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';

const MHP_URL = 'https://www.magichomephotos.com/';
const SCRIPT_ID = 'json-ld-magic-home-photos';
const LINK_REL = 'related';

/**
 * Head wiring for the Magic Home Photos partner product.
 *
 * Emits two things on the pages that reference it (home + blog):
 *  1. <link rel="related" href="https://www.magichomephotos.com/"> — a machine
 *     readable pointer to the canonical Magic Home Photos site. The page's own
 *     <link rel="canonical"> stays self-referential (pointing it at an external
 *     domain would tell Google to drop this page from the index).
 *  2. A Product node with an Offer whose `url` is the Magic Home Photos site,
 *     so the offer is actionable where it actually lives.
 */
export function MagicHomePhotosSeo() {
  const { language } = useLanguage();

  useEffect(() => {
    const lang = language === 'dk' ? 'da' : language;

    let link = document.head.querySelector<HTMLLinkElement>(
      `link[rel="${LINK_REL}"][href="${MHP_URL}"]`,
    );
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', LINK_REL);
      link.setAttribute('href', MHP_URL);
      document.head.appendChild(link);
    }

    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.setAttribute('type', 'application/ld+json');
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': `${MHP_URL}#product`,
      name: 'Magic Home Photos',
      url: MHP_URL,
      inLanguage: lang,
      category: 'AI photo enhancement for vacation rentals',
      description:
        'AI photo enhancement for professional vacation rental companies: upload property photos and get clear, professional images within minutes.',
      brand: { '@type': 'Brand', name: 'Magic Home Photos' },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        description: '15 free photos and 1 free video to try',
        url: MHP_URL,
      },
    });

    return () => {
      document.getElementById(SCRIPT_ID)?.remove();
      document.head
        .querySelectorAll(`link[rel="${LINK_REL}"][href="${MHP_URL}"]`)
        .forEach((el) => el.remove());
    };
  }, [language]);

  return null;
}

export default MagicHomePhotosSeo;
